import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  addAllowlistEntry,
  addWorkspaceDomain,
  convertToWorkspace,
  getWorkspaceAdmin,
  removeAllowlistEntry,
  removeWorkspaceDomain,
  updateWorkspaceName,
  WorkspaceAdmin,
} from "../../services/AdminWorkspaces";

type Result = { success: boolean; data?: WorkspaceAdmin; errors?: Array<{ title?: string; detail?: string }> };

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

/**
 * Staff: an organization's own address (<slug>.queri.law), display name,
 * who may join by themselves (allowlist) and email domains that join
 * automatically.
 */
const WorkspacePanel: React.FC<{ organizationId: string; organizationName: string; onChanged: () => void }> = ({
  organizationId,
  organizationName,
  onChanged,
}) => {
  const [ws, setWs] = useState<WorkspaceAdmin | null>(null);
  const [busy, setBusy] = useState(false);
  const [slug, setSlug] = useState(slugify(organizationName));
  const [displayName, setDisplayName] = useState(organizationName);
  const [allowEmail, setAllowEmail] = useState("");
  const [domain, setDomain] = useState("");
  const [autoJoin, setAutoJoin] = useState(false);

  const load = useCallback(async () => {
    const res = await getWorkspaceAdmin(organizationId);
    if (res.success && res.data) {
      setWs(res.data);
      setDisplayName(res.data.displayName || res.data.name);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = async (action: () => Promise<Result>, done: string) => {
    setBusy(true);
    try {
      const res = await action();
      if (res.success && res.data) {
        setWs(res.data);
        toast.success(done);
        onChanged();
        return true;
      }
      toast.error(res.errors?.[0]?.detail || res.errors?.[0]?.title || "Could not save");
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!ws) return null;

  if (!ws.isWorkspace) {
    return (
      <Card className="p-4">
        <h2 className="text-base font-semibold">Workspace</h2>
        <p className="mb-3 text-sm text-muted-foreground">
          Not a workspace. Making it one gives it its own address, branding and email-only invitations; members and data stay.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="ws-slug">Address</Label>
            <div className="flex items-center gap-1 text-sm">
              <Input id="ws-slug" className="w-40" value={slug} onChange={(e) => setSlug(slugify(e.target.value))} />
              <span className="text-muted-foreground">.queri.law</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ws-name">Display name</Label>
            <Input id="ws-name" className="w-56" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <Button
            disabled={busy || !slug}
            onClick={() => void apply(() => convertToWorkspace(organizationId, slug, displayName), `Now a workspace: ${slug}.queri.law`)}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Make it a workspace
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          The address also needs a DNS record and an ingress host before it opens.
        </p>
      </Card>
    );
  }

  return (
    <Card className="space-y-5 p-4">
      <div>
        <h2 className="text-base font-semibold">Workspace</h2>
        <p className="text-sm text-muted-foreground">
          {ws.slug}.queri.law · members join by invitation, the allowlist or an email domain below.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="ws-display">Display name</Label>
          <Input id="ws-display" className="w-64" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <Button
          variant="outline"
          disabled={busy || !displayName.trim() || displayName === (ws.displayName || ws.name)}
          onClick={() => void apply(() => updateWorkspaceName(organizationId, displayName.trim()), "Display name saved")}
        >
          Save
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Allowlist</Label>
        <p className="text-xs text-muted-foreground">These people can sign up on the website and land in this workspace. No email is sent.</p>
        <div className="flex flex-wrap gap-1.5">
          {ws.allowlist.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs">
              {a.email || `@${a.domain}`}
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${a.email || a.domain}`}
                disabled={busy}
                onClick={() => void apply(() => removeAllowlistEntry(organizationId, a.id), "Removed from the allowlist")}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {ws.allowlist.length === 0 && <span className="text-sm text-muted-foreground">Nobody yet.</span>}
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = allowEmail.trim();
            if (!value) return;
            void apply(() => addAllowlistEntry(organizationId, { email: value }), `${value} added to the allowlist`).then(
              (ok) => ok && setAllowEmail(""),
            );
          }}
        >
          <Input className="w-72" type="email" placeholder="name@company.com" value={allowEmail} onChange={(e) => setAllowEmail(e.target.value)} />
          <Button type="submit" variant="outline" disabled={busy || !allowEmail.trim()}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </form>
      </div>

      <div className="space-y-2">
        <Label>Email domains</Label>
        <p className="text-xs text-muted-foreground">
          With auto-join, anyone with an address at the domain joins this workspace when they sign up. Use only the customer's own domain.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {ws.domains.map((d) => (
            <span key={d.id} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs">
              @{d.domain}
              {d.autoJoin && <span className="text-muted-foreground">· auto-join</span>}
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${d.domain}`}
                disabled={busy}
                onClick={() => void apply(() => removeWorkspaceDomain(organizationId, d.id), "Domain removed")}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {ws.domains.length === 0 && <span className="text-sm text-muted-foreground">None.</span>}
        </div>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = domain.trim().replace(/^@/, "");
            if (!value) return;
            void apply(() => addWorkspaceDomain(organizationId, value, autoJoin), `@${value} added`).then(
              (ok) => ok && setDomain(""),
            );
          }}
        >
          <Input className="w-56" placeholder="company.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={autoJoin} onCheckedChange={setAutoJoin} />
            Auto-join
          </label>
          <Button type="submit" variant="outline" disabled={busy || !domain.trim()}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </form>
      </div>
    </Card>
  );
};

export default WorkspacePanel;
